import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function HRInterview() {
  const { applicationId } = useParams();
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchQuestions() {
      try {
        const response = await axios.get(`/api/ai-interview/context/${applicationId}`);
        if (response.data.generatedQuestions && Array.isArray(response.data.generatedQuestions)) {
          setQuestions(response.data.generatedQuestions);
        } else {
          // Fallback if no questions returned
          setQuestions([
            'Tell us about yourself',
            'Why do you want to work for our company?',
            'What are your career goals?',
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch HR questions:', err);
        setError('Failed to load interview questions.');
        // Fallback
        setQuestions([
          'Tell us about yourself',
          'Why do you want to work for our company?',
          'What are your career goals?',
        ]);
      } finally {
        setLoading(false);
      }
    }
    fetchQuestions();
  }, [applicationId]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await axios.post(`/api/hr-interview/${applicationId}/submit`, { answers });
      alert('HR interview submitted successfully!');
      navigate('/candidate/dashboard');
    } catch (err) {
      console.error('Failed to submit HR interview:', err);
      setError('Failed to submit interview. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          HR Interview
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Answer the following HR questions
        </Alert>

        {questions.map((question, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Question {index + 1}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {question}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={answers[index] || ''}
              onChange={(e) => setAnswers({ ...answers, [index]: e.target.value })}
              placeholder="Your answer..."
              sx={{ mt: 1 }}
            />
          </Box>
        ))}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || questions.length === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Interview'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default HRInterview;
